// frontend/src/components/Dashboard/ProductForm.jsx
import React, { useState, useEffect } from "react";
import { createProduct, updateProduct } from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTimes,
  faEdit,
  faTrash,
  faImage,
  faTag,
  faBox,
  faUpload,
  faDollarSign,
  faWeightScale,
  faTruck,
  faStar,
  faClipboard,
  faCheck,
  faSpinner,
  faExclamationTriangle,
  faInfoCircle,
  faSave,
  faGlobe,
  faShip,
  faRuler,
  faBoxes,faCheckCircle,
  faBuilding,
  faAnchor,
  faLocationDot,
  faCalendarCheck,
  faClock,
  faRocket,
  faLeaf,
  faCow,
  faTractor,
  faShoppingCart,
  faUsers,
  faMobileAlt,
  faMoneyBillWave,
  faCreditCard,
  faWallet,
  faStore,
  faCrown,
  faShieldAlt,
  faUserCheck,
  faList,
  faSlidersH,
  faTags,
  faLayerGroup,
  faPalette,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
  
// ============================================
// BRAND & SUPPLIER CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
  city: "Kuala Lumpur",
  portLoading: "Port Klang, Malaysia",
  portDischarge: "Freetown, Sierra Leone",
};

const AVAILABLE_SIZES = [2, 10, 25, 50];
const PREORDER_SIZES = [25, 50];
const CATEGORIES = [
  { value: "rumen-bypass-fat", label: "Rumen Bypass Fat", icon: faCow },
  { value: "mineral-blocks", label: "Mineral Blocks", icon: faStar },
  {
    value: "protein-concentrates",
    label: "Protein Concentrates",
    icon: faLeaf,
  },
  { value: "feed-supplements", label: "Feed Supplements", icon: faBox },
];

// ============================================
// MAIN COMPONENT
// ============================================
const ProductForm = ({ product, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("basic");
  const [validationErrors, setValidationErrors] = useState({});
  const [newBenefit, setNewBenefit] = useState("");
  const [newSpecKey, setNewSpecKey] = useState("");
  const [newSpecValue, setNewSpecValue] = useState("");
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState(product?.images || []);
  const [showTiers, setShowTiers] = useState(true);
  const [showBulkOptions, setShowBulkOptions] = useState(true);
  const [showSupplierInfo, setShowSupplierInfo] = useState(true);

  const [formData, setFormData] = useState({
    name: product?.name || "",
    slug: product?.slug || "",
    category: product?.category || "rumen-bypass-fat",
    description: product?.description || "",
    shortDescription: product?.shortDescription || "",
    basePrice: {
      usd: product?.basePrice?.usd || 0,
      sll: product?.basePrice?.sll || 0,
      myr: product?.basePrice?.myr || 0,
    },
    unit: product?.unit || "kg",
    unitSize: product?.unitSize || 1,
    packaging: product?.packaging || "25kg bag",
    stock: product?.stock || 0,
    lowStockThreshold: product?.lowStockThreshold || 100,
    isAvailable: product?.isAvailable !== false,
    isPreOrderAvailable: product?.isPreOrderAvailable !== false,
    featured: product?.featured || false,
    featuredOrder: product?.featuredOrder || 0,
    origin: product?.origin || SUPPLIER.country,
    supplierName: product?.supplierName || SUPPLIER.name,
    supplierCountry: product?.supplierCountry || SUPPLIER.country,
    supplierCity: product?.supplierCity || SUPPLIER.city,
    portLoading: product?.portLoading || SUPPLIER.portLoading,
    portDischarge: product?.portDischarge || SUPPLIER.portDischarge,
    farmerMinimumOrder: product?.farmerMinimumOrder || 25,
    usageInstructions: product?.usageInstructions || "",
    shippingWeight: product?.shippingWeight || 1,
    hazmat: product?.hazmat || false,
    availableSizes: product?.availableSizes || [...AVAILABLE_SIZES],
    preOrderSizes: product?.preOrderSizes || [...PREORDER_SIZES],
    priceTiers:
      product?.priceTiers?.length > 0
        ? product.priceTiers
        : [
            {
              userType: "customer",
              minQuantity: 1,
              maxQuantity: 24,
              discountPercent: 0,
            },
            {
              userType: "customer",
              minQuantity: 25,
              maxQuantity: 99,
              discountPercent: 5,
            },
            {
              userType: "customer",
              minQuantity: 100,
              maxQuantity: 499,
              discountPercent: 15,
            },
            {
              userType: "farmer",
              minQuantity: 25,
              maxQuantity: 99,
              discountPercent: 10,
            },
            {
              userType: "farmer",
              minQuantity: 100,
              maxQuantity: 499,
              discountPercent: 20,
            },
            {
              userType: "farmer",
              minQuantity: 500,
              maxQuantity: null,
              discountPercent: 30,
            },
          ],
    bulkOptions:
      product?.bulkOptions?.length > 0
        ? product.bulkOptions
        : [
            {
              quantity: 25,
              label: "25kg (Starter)",
              discountPercent: 5,
              isActive: true,
            },
            {
              quantity: 50,
              label: "50kg (Farmer)",
              discountPercent: 10,
              isActive: true,
            },
            {
              quantity: 100,
              label: "100kg (Wholesale)",
              discountPercent: 20,
              isActive: true,
            },
            {
              quantity: 500,
              label: "500kg (Bulk Farmer)",
              discountPercent: 30,
              isActive: true,
            },
            {
              quantity: 1000,
              label: "1 Ton (Enterprise)",
              discountPercent: 40,
              isActive: true,
            },
          ],
    benefits: product?.benefits || [],
    specifications: product?.specifications || {},
    metaTitle: product?.metaTitle || "",
    metaDescription: product?.metaDescription || "",
    metaKeywords: product?.metaKeywords || [],
    averageRating: product?.averageRating || 0,
    commentCount: product?.commentCount || 0,
  });

  // Auto-generate slug from name
  useEffect(() => {
    if (!product && formData.name) {
      const slug = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      setFormData((prev) => ({ ...prev, slug }));
    }
  }, [formData.name, product]);

  // Auto-generate meta title if empty
  useEffect(() => {
    if (!formData.metaTitle && formData.name) {
      setFormData((prev) => ({
        ...prev,
        metaTitle: `${formData.name} - Kenali Enterprise | Premium Livestock Feed`,
      }));
    }
  }, [formData.name, formData.metaTitle]);

  // Set default supplier info
  useEffect(() => {
    if (!product) {
      setFormData((prev) => ({
        ...prev,
        origin: prev.origin || SUPPLIER.country,
        supplierName: prev.supplierName || SUPPLIER.name,
        supplierCountry: prev.supplierCountry || SUPPLIER.country,
        supplierCity: prev.supplierCity || SUPPLIER.city,
        portLoading: prev.portLoading || SUPPLIER.portLoading,
        portDischarge: prev.portDischarge || SUPPLIER.portDischarge,
      }));
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else if (name === "priceUSD") {
      const usdPrice = parseFloat(value) || 0;
      setFormData({
        ...formData,
        basePrice: {
          ...formData.basePrice,
          usd: usdPrice,
          sll: usdPrice * 23500,
          myr: usdPrice * 4.75,
        },
      });
    } else if (name === "availableSizes") {
      const sizes = value
        .split(",")
        .map((s) => parseInt(s.trim()))
        .filter((s) => !isNaN(s) && s > 0);
      setFormData({
        ...formData,
        availableSizes: sizes.length ? sizes : [...AVAILABLE_SIZES],
      });
    } else if (name === "preOrderSizes") {
      const sizes = value
        .split(",")
        .map((s) => parseInt(s.trim()))
        .filter((s) => !isNaN(s) && s > 0);
      setFormData({
        ...formData,
        preOrderSizes: sizes.length ? sizes : [...PREORDER_SIZES],
      });
    } else if (name.startsWith("bulkOptions.")) {
      const [_, index, field] = name.split(".");
      handleBulkOptionChange(parseInt(index), field, value);
    } else if (name.startsWith("priceTiers.")) {
      const [_, index, field] = name.split(".");
      handleTierChange(parseInt(index), field, value);
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setError("");
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleTierChange = (index, field, value) => {
    const updatedTiers = [...formData.priceTiers];
    if (field === "maxQuantity" && value === "") {
      updatedTiers[index][field] = null;
    } else if (field === "userType") {
      updatedTiers[index][field] = value;
    } else {
      updatedTiers[index][field] =
        field === "discountPercent"
          ? parseFloat(value) || 0
          : parseInt(value) || 0;
    }
    setFormData({ ...formData, priceTiers: updatedTiers });
  };

  const addTier = () => {
    setFormData({
      ...formData,
      priceTiers: [
        ...formData.priceTiers,
        {
          userType: "farmer",
          minQuantity: 0,
          maxQuantity: null,
          discountPercent: 0,
        },
      ],
    });
  };

  const removeTier = (index) => {
    if (formData.priceTiers.length <= 1) {
      setError("Must have at least one price tier");
      return;
    }
    const updatedTiers = formData.priceTiers.filter((_, i) => i !== index);
    setFormData({ ...formData, priceTiers: updatedTiers });
  };

  const handleBulkOptionChange = (index, field, value) => {
    const updatedOptions = [...formData.bulkOptions];
    if (field === "isActive") {
      updatedOptions[index][field] = value;
    } else if (field === "label") {
      updatedOptions[index][field] = value;
    } else {
      updatedOptions[index][field] =
        field === "discountPercent"
          ? parseFloat(value) || 0
          : parseInt(value) || 0;
    }
    setFormData({ ...formData, bulkOptions: updatedOptions });
  };

  const addBulkOption = () => {
    setFormData({
      ...formData,
      bulkOptions: [
        ...formData.bulkOptions,
        { quantity: 0, label: "", discountPercent: 0, isActive: true },
      ],
    });
  };

  const removeBulkOption = (index) => {
    if (formData.bulkOptions.length <= 1) {
      setError("Must have at least one bulk option");
      return;
    }
    const updatedOptions = formData.bulkOptions.filter((_, i) => i !== index);
    setFormData({ ...formData, bulkOptions: updatedOptions });
  };

  const handleAddBenefit = () => {
    if (newBenefit.trim()) {
      setFormData({
        ...formData,
        benefits: [...formData.benefits, newBenefit.trim()],
      });
      setNewBenefit("");
    }
  };

  const handleRemoveBenefit = (index) => {
    const updatedBenefits = formData.benefits.filter((_, i) => i !== index);
    setFormData({ ...formData, benefits: updatedBenefits });
  };

  const handleAddSpecification = () => {
    if (newSpecKey.trim() && newSpecValue.trim()) {
      setFormData({
        ...formData,
        specifications: {
          ...formData.specifications,
          [newSpecKey.trim()]: newSpecValue.trim(),
        },
      });
      setNewSpecKey("");
      setNewSpecValue("");
    }
  };

  const handleRemoveSpecification = (key) => {
    const updatedSpecs = { ...formData.specifications };
    delete updatedSpecs[key];
    setFormData({ ...formData, specifications: updatedSpecs });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => file.size <= 5 * 1024 * 1024);
    if (validFiles.length !== files.length) {
      setError("Some files exceed the 5MB limit");
    }
    setImages([...images, ...validFiles]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Product name is required";
    if (!formData.slug.trim()) errors.slug = "Slug is required";
    if (!formData.description.trim())
      errors.description = "Description is required";
    if (!formData.basePrice.usd || formData.basePrice.usd <= 0) {
      errors.priceUSD = "Base price must be greater than 0";
    }
    if (formData.stock < 0) errors.stock = "Stock cannot be negative";
    if (formData.lowStockThreshold < 0) {
      errors.lowStockThreshold = "Low stock threshold cannot be negative";
    }
    if (formData.farmerMinimumOrder < 0) {
      errors.farmerMinimumOrder = "Farmer minimum order cannot be negative";
    }
    if (!formData.origin.trim()) errors.origin = "Origin is required";
    if (!formData.availableSizes.length) {
      errors.availableSizes = "At least one available size is required";
    }

    formData.priceTiers.forEach((tier, index) => {
      if (tier.minQuantity < 0) {
        errors[`priceTiers.${index}.minQuantity`] =
          "Min quantity cannot be negative";
      }
      if (tier.discountPercent < 0 || tier.discountPercent > 50) {
        errors[`priceTiers.${index}.discountPercent`] =
          "Discount must be between 0-50%";
      }
    });

    formData.bulkOptions.forEach((option, index) => {
      if (option.quantity < 0) {
        errors[`bulkOptions.${index}.quantity`] = "Quantity cannot be negative";
      }
      if (option.discountPercent < 0 || option.discountPercent > 50) {
        errors[`bulkOptions.${index}.discountPercent`] =
          "Discount must be between 0-50%";
      }
      if (!option.label.trim()) {
        errors[`bulkOptions.${index}.label`] = "Label is required";
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Please fix all validation errors before submitting");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const data = new FormData();

      const fieldsToSend = {
        name: formData.name.trim(),
        slug: formData.slug.trim().toLowerCase(),
        category: formData.category,
        description: formData.description.trim(),
        shortDescription: formData.shortDescription || "",
        basePrice: JSON.stringify(formData.basePrice),
        unit: formData.unit,
        unitSize: formData.unitSize,
        packaging: formData.packaging,
        stock: formData.stock,
        lowStockThreshold: formData.lowStockThreshold,
        isAvailable: formData.isAvailable,
        isPreOrderAvailable: formData.isPreOrderAvailable,
        featured: formData.featured,
        featuredOrder: formData.featuredOrder,
        origin: formData.origin || SUPPLIER.country,
        supplierName: formData.supplierName || SUPPLIER.name,
        supplierCountry: formData.supplierCountry || SUPPLIER.country,
        supplierCity: formData.supplierCity || SUPPLIER.city,
        portLoading: formData.portLoading || SUPPLIER.portLoading,
        portDischarge: formData.portDischarge || SUPPLIER.portDischarge,
        farmerMinimumOrder: formData.farmerMinimumOrder,
        usageInstructions: formData.usageInstructions || "",
        shippingWeight: formData.shippingWeight,
        hazmat: formData.hazmat,
        availableSizes: JSON.stringify(formData.availableSizes),
        preOrderSizes: JSON.stringify(formData.preOrderSizes),
        priceTiers: JSON.stringify(formData.priceTiers),
        bulkOptions: JSON.stringify(formData.bulkOptions),
        benefits: JSON.stringify(formData.benefits),
        specifications: JSON.stringify(formData.specifications),
        metaTitle: formData.metaTitle || `${formData.name} - Kenali Enterprise`,
        metaDescription:
          formData.metaDescription || formData.description.substring(0, 160),
        metaKeywords: JSON.stringify(formData.metaKeywords),
      };

      Object.entries(fieldsToSend).forEach(([key, value]) => {
        data.append(key, value);
      });

      images.forEach((img) => data.append("images", img));
      if (existingImages.length > 0) {
        data.append("existingImages", JSON.stringify(existingImages));
      }

      let response;
      if (product) {
        response = await updateProduct(product._id, data);
      } else {
        response = await createProduct(data);
      }

      if (response.data.success) {
        setSuccess("Product saved successfully!");
        setTimeout(() => {
          if (onSave) onSave(response.data.data);
          if (onClose) onClose();
        }, 1500);
      }
    } catch (error) {
      console.error("Error saving product:", error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "Error saving product. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const renderTabs = () => {
    const tabs = [
      { id: "basic", label: "Basic", icon: faTag },
      { id: "pricing", label: "Pricing", icon: faDollarSign },
      { id: "descriptions", label: "Descriptions", icon: faClipboard },
      { id: "specs", label: "Specs", icon: faInfoCircle },
      { id: "inventory", label: "Inventory", icon: faBox },
      { id: "images", label: "Images", icon: faImage },
      { id: "supplier", label: "Supplier", icon: faShip },
      { id: "seo", label: "SEO", icon: faGlobe },
      { id: "status", label: "Status", icon: faCheck },
    ];

    return (
      <div className="form-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={activeTab === tab.id ? "active" : ""}
            onClick={() => setActiveTab(tab.id)}
          >
            <FontAwesomeIcon icon={tab.icon} /> {tab.label}
          </button>
        ))}
      </div>
    );
  };

  const renderBasicTab = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faTag} /> Basic Information
      </h3>

      <div className="form-row">
        <div className="form-group">
          <label>Product Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={validationErrors.name ? "error" : ""}
            placeholder="e.g., Kenali GrowPro™"
          />
          {validationErrors.name && (
            <span className="error-text">{validationErrors.name}</span>
          )}
        </div>
        <div className="form-group">
          <label>Slug *</label>
          <input
            type="text"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            className={validationErrors.slug ? "error" : ""}
            placeholder="e.g., kenali-growpro"
          />
          {validationErrors.slug && (
            <span className="error-text">{validationErrors.slug}</span>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Category *</label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faGlobe} /> Origin *
          </label>
          <input
            type="text"
            name="origin"
            value={formData.origin}
            onChange={handleChange}
            placeholder="e.g., Malaysia"
            className={validationErrors.origin ? "error" : ""}
          />
          {validationErrors.origin && (
            <span className="error-text">{validationErrors.origin}</span>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faWeightScale} /> Unit
          </label>
          <select name="unit" value={formData.unit} onChange={handleChange}>
            <option value="kg">Kilogram (kg)</option>
            <option value="ton">Ton</option>
            <option value="block">Block</option>
            <option value="bag">Bag</option>
          </select>
        </div>
        <div className="form-group">
          <label>Unit Size</label>
          <input
            type="number"
            name="unitSize"
            value={formData.unitSize}
            onChange={handleChange}
            min="1"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Packaging</label>
          <input
            type="text"
            name="packaging"
            value={formData.packaging}
            onChange={handleChange}
            placeholder="e.g., 25kg bag"
          />
        </div>
        <div className="form-group">
          <label>Short Description</label>
          <input
            type="text"
            name="shortDescription"
            value={formData.shortDescription}
            onChange={handleChange}
            placeholder="Brief summary for product listings"
          />
        </div>
      </div>

      {/* Available Sizes */}
      <div className="form-row">
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faRuler} /> Available Sizes (kg)
            <small className="helper-text">
              Comma separated, e.g., 2, 10, 25, 50
            </small>
          </label>
          <input
            type="text"
            name="availableSizes"
            value={formData.availableSizes.join(", ")}
            onChange={handleChange}
            placeholder="2, 10, 25, 50"
            className={validationErrors.availableSizes ? "error" : ""}
          />
          {validationErrors.availableSizes && (
            <span className="error-text">
              {validationErrors.availableSizes}
            </span>
          )}
          <div className="size-presets">
            {AVAILABLE_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                className="size-preset-btn"
                onClick={() => {
                  const current = new Set(formData.availableSizes);
                  if (current.has(size)) current.delete(size);
                  else current.add(size);
                  setFormData({
                    ...formData,
                    availableSizes: Array.from(current).sort((a, b) => a - b),
                  });
                }}
                style={{
                  background: formData.availableSizes.includes(size)
                    ? "#D9A321"
                    : "#f0f0f0",
                  color: formData.availableSizes.includes(size)
                    ? "#fff"
                    : "#1f1f1f",
                }}
              >
                {size}kg
              </button>
            ))}
          </div>
        </div>
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faBoxes} /> Pre-Order Sizes (kg)
            <small className="helper-text">Comma separated, e.g., 25, 50</small>
          </label>
          <input
            type="text"
            name="preOrderSizes"
            value={formData.preOrderSizes.join(", ")}
            onChange={handleChange}
            placeholder="25, 50"
          />
          <div className="size-presets">
            {PREORDER_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                className="size-preset-btn"
                onClick={() => {
                  const current = new Set(formData.preOrderSizes);
                  if (current.has(size)) current.delete(size);
                  else current.add(size);
                  setFormData({
                    ...formData,
                    preOrderSizes: Array.from(current).sort((a, b) => a - b),
                  });
                }}
                style={{
                  background: formData.preOrderSizes.includes(size)
                    ? "#8b5cf6"
                    : "#f0f0f0",
                  color: formData.preOrderSizes.includes(size)
                    ? "#fff"
                    : "#1f1f1f",
                }}
              >
                {size}kg
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPricingTab = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faDollarSign} /> Pricing
      </h3>

      <div className="form-row">
        <div className="form-group">
          <label>Base Price (USD per {formData.unit}) *</label>
          <input
            type="number"
            name="priceUSD"
            value={formData.basePrice.usd}
            onChange={handleChange}
            step="0.01"
            min="0.01"
            className={validationErrors.priceUSD ? "error" : ""}
          />
          {validationErrors.priceUSD && (
            <span className="error-text">{validationErrors.priceUSD}</span>
          )}
          <small className="helper-text">
            <FontAwesomeIcon icon={faMoneyBillWave} /> SLL:{" "}
            {formData.basePrice.sll?.toLocaleString() || 0} |
            <FontAwesomeIcon icon={faCreditCard} /> MYR:{" "}
            {formData.basePrice.myr?.toFixed(2) || 0}
          </small>
        </div>
        <div className="form-group">
          <label>Farmer Minimum Order (kg)</label>
          <input
            type="number"
            name="farmerMinimumOrder"
            value={formData.farmerMinimumOrder}
            onChange={handleChange}
            min="0"
            className={validationErrors.farmerMinimumOrder ? "error" : ""}
          />
          {validationErrors.farmerMinimumOrder && (
            <span className="error-text">
              {validationErrors.farmerMinimumOrder}
            </span>
          )}
        </div>
      </div>

      {/* Price Tiers */}
      <div className="tiers-toggle">
        <button
          type="button"
          onClick={() => setShowTiers(!showTiers)}
          className="toggle-btn"
        >
          <FontAwesomeIcon icon={showTiers ? faEye : faEyeSlash} />
          {showTiers ? " Hide" : " Show"} Price Tiers
        </button>
      </div>
      {showTiers && (
        <div className="price-tiers-section">
          <h4>
            <FontAwesomeIcon icon={faUsers} /> Discount Tiers by User Type
          </h4>
          <div className="tiers-header">
            <span>User Type</span>
            <span>Min Qty</span>
            <span>Max Qty</span>
            <span>Discount %</span>
            <span></span>
          </div>
          {formData.priceTiers.map((tier, index) => (
            <div key={index} className="tier-row">
              <select
                value={tier.userType}
                onChange={(e) =>
                  handleTierChange(index, "userType", e.target.value)
                }
              >
                <option value="customer">🛒 Customer</option>
                <option value="farmer">🌾 Farmer</option>
              </select>
              <input
                type="number"
                placeholder="Min"
                value={tier.minQuantity}
                onChange={(e) =>
                  handleTierChange(index, "minQuantity", e.target.value)
                }
                min="0"
                className={
                  validationErrors[`priceTiers.${index}.minQuantity`]
                    ? "error"
                    : ""
                }
              />
              <input
                type="number"
                placeholder="Max (∞)"
                value={tier.maxQuantity || ""}
                onChange={(e) =>
                  handleTierChange(index, "maxQuantity", e.target.value)
                }
                min="0"
              />
              <input
                type="number"
                placeholder="%"
                value={tier.discountPercent}
                onChange={(e) =>
                  handleTierChange(index, "discountPercent", e.target.value)
                }
                step="1"
                min="0"
                max="100"
                className={
                  validationErrors[`priceTiers.${index}.discountPercent`]
                    ? "error"
                    : ""
                }
              />
              <button
                type="button"
                onClick={() => removeTier(index)}
                className="remove-btn"
                disabled={formData.priceTiers.length <= 1}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addTier} className="add-btn">
            <FontAwesomeIcon icon={faPlus} /> Add Tier
          </button>
        </div>
      )}

      {/* Bulk Options */}
      <div className="tiers-toggle">
        <button
          type="button"
          onClick={() => setShowBulkOptions(!showBulkOptions)}
          className="toggle-btn"
        >
          <FontAwesomeIcon icon={showBulkOptions ? faEye : faEyeSlash} />
          {showBulkOptions ? " Hide" : " Show"} Bulk Options
        </button>
      </div>
      {showBulkOptions && (
        <div className="bulk-options-section">
          <h4>
            <FontAwesomeIcon icon={faBox} /> Bulk Options (Quick Select)
          </h4>
          <div className="bulk-header">
            <span>Qty (kg)</span>
            <span>Label</span>
            <span>Discount %</span>
            <span>Active</span>
            <span></span>
          </div>
          {formData.bulkOptions.map((option, index) => (
            <div key={index} className="bulk-row">
              <input
                type="number"
                placeholder="Qty"
                value={option.quantity}
                onChange={(e) =>
                  handleBulkOptionChange(index, "quantity", e.target.value)
                }
                min="0"
                className={
                  validationErrors[`bulkOptions.${index}.quantity`]
                    ? "error"
                    : ""
                }
              />
              <input
                type="text"
                placeholder="Label"
                value={option.label}
                onChange={(e) =>
                  handleBulkOptionChange(index, "label", e.target.value)
                }
                className={
                  validationErrors[`bulkOptions.${index}.label`] ? "error" : ""
                }
              />
              <input
                type="number"
                placeholder="%"
                value={option.discountPercent}
                onChange={(e) =>
                  handleBulkOptionChange(
                    index,
                    "discountPercent",
                    e.target.value,
                  )
                }
                step="1"
                min="0"
                max="100"
                className={
                  validationErrors[`bulkOptions.${index}.discountPercent`]
                    ? "error"
                    : ""
                }
              />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={option.isActive}
                  onChange={(e) =>
                    handleBulkOptionChange(index, "isActive", e.target.checked)
                  }
                />
              </label>
              <button
                type="button"
                onClick={() => removeBulkOption(index)}
                className="remove-btn"
                disabled={formData.bulkOptions.length <= 1}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addBulkOption} className="add-btn">
            <FontAwesomeIcon icon={faPlus} /> Add Bulk Option
          </button>
        </div>
      )}
    </div>
  );

  const renderDescriptionsTab = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faClipboard} /> Descriptions
      </h3>

      <div className="form-group">
        <label>Full Description *</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="5"
          className={validationErrors.description ? "error" : ""}
          placeholder="Detailed product description with features and benefits..."
        />
        {validationErrors.description && (
          <span className="error-text">{validationErrors.description}</span>
        )}
        <small>{formData.description.length} characters</small>
      </div>

      <div className="form-group">
        <label>Usage Instructions</label>
        <textarea
          name="usageInstructions"
          value={formData.usageInstructions}
          onChange={handleChange}
          rows="3"
          placeholder="How to use this product..."
        />
      </div>

      <div className="form-group">
        <label>
          <FontAwesomeIcon icon={faStar} /> Benefits
        </label>
        <div className="tags-list">
          {formData.benefits.map((benefit, index) => (
            <div key={index} className="tag-item">
              <span>{benefit}</span>
              <button type="button" onClick={() => handleRemoveBenefit(index)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
        </div>
        <div className="add-tag-row">
          <input
            type="text"
            placeholder="Add benefit (e.g., Boosts milk production)"
            value={newBenefit}
            onChange={(e) => setNewBenefit(e.target.value)}
            onKeyPress={(e) =>
              e.key === "Enter" && (e.preventDefault(), handleAddBenefit())
            }
          />
          <button type="button" onClick={handleAddBenefit} className="add-btn">
            <FontAwesomeIcon icon={faPlus} /> Add
          </button>
        </div>
      </div>
    </div>
  );

  const renderSpecsTab = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faInfoCircle} /> Specifications
      </h3>
      <div className="specs-list">
        {Object.entries(formData.specifications).map(([key, value]) => (
          <div key={key} className="spec-item">
            <span className="spec-key">{key}:</span>
            <span className="spec-value">{value}</span>
            <button
              type="button"
              onClick={() => handleRemoveSpecification(key)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        ))}
      </div>
      <div className="add-spec-row">
        <input
          type="text"
          placeholder="Specification name"
          value={newSpecKey}
          onChange={(e) => setNewSpecKey(e.target.value)}
        />
        <input
          type="text"
          placeholder="Value"
          value={newSpecValue}
          onChange={(e) => setNewSpecValue(e.target.value)}
        />
        <button
          type="button"
          onClick={handleAddSpecification}
          className="add-btn"
        >
          <FontAwesomeIcon icon={faPlus} /> Add
        </button>
      </div>
    </div>
  );

  const renderInventoryTab = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faBox} /> Inventory
      </h3>

      <div className="form-row">
        <div className="form-group">
          <label>Stock Quantity</label>
          <input
            type="number"
            name="stock"
            value={formData.stock}
            onChange={handleChange}
            min="0"
            className={validationErrors.stock ? "error" : ""}
          />
          {validationErrors.stock && (
            <span className="error-text">{validationErrors.stock}</span>
          )}
        </div>
        <div className="form-group">
          <label>Low Stock Threshold</label>
          <input
            type="number"
            name="lowStockThreshold"
            value={formData.lowStockThreshold}
            onChange={handleChange}
            min="0"
            className={validationErrors.lowStockThreshold ? "error" : ""}
          />
          {validationErrors.lowStockThreshold && (
            <span className="error-text">
              {validationErrors.lowStockThreshold}
            </span>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Shipping Weight (kg)</label>
          <input
            type="number"
            name="shippingWeight"
            value={formData.shippingWeight}
            onChange={handleChange}
            step="0.01"
            min="0"
          />
        </div>
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="hazmat"
              checked={formData.hazmat}
              onChange={handleChange}
            />
            <FontAwesomeIcon icon={faExclamationTriangle} /> Hazardous Material
          </label>
        </div>
      </div>
    </div>
  );

  const renderImagesTab = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faImage} /> Product Images
      </h3>

      <div className="form-group">
        <label>Upload Images</label>
        <div className="file-upload-area">
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="file-input"
            id="product-images"
          />
          <label htmlFor="product-images" className="file-upload-label">
            <FontAwesomeIcon icon={faUpload} />
            <span>Click or drag to upload images</span>
            <small>
              Max 5 images, 5MB each. Supported: JPG, PNG, WEBP, GIF
            </small>
          </label>
        </div>
      </div>

      {images.length > 0 && (
        <div className="image-preview-grid">
          {images.map((img, index) => (
            <div key={index} className="image-preview-item">
              <img src={URL.createObjectURL(img)} alt={`Preview ${index}`} />
              <button type="button" onClick={() => removeImage(index)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
        </div>
      )}

      {existingImages.length > 0 && (
        <div className="image-preview-grid">
          {existingImages.map((img, index) => (
            <div key={index} className="image-preview-item existing">
              <img src={img} alt={`Product ${index}`} />
              <button type="button" onClick={() => removeExistingImage(index)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSupplierTab = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faShip} /> Supplier Information
      </h3>

      <div className="supplier-info-card">
        <FontAwesomeIcon icon={faShip} className="supplier-icon" />
        <div className="supplier-info">
          <strong>{SUPPLIER.name}</strong>
          <span>
            <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} &bull;
            <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
          </span>
        </div>
        <span className="supplier-badge">
          <FontAwesomeIcon icon={faCheckCircle} /> Quality Assured
        </span>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faBuilding} /> Supplier Name
          </label>
          <input
            type="text"
            name="supplierName"
            value={formData.supplierName}
            onChange={handleChange}
            placeholder={SUPPLIER.name}
          />
        </div>
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faGlobe} /> Supplier Country
          </label>
          <input
            type="text"
            name="supplierCountry"
            value={formData.supplierCountry}
            onChange={handleChange}
            placeholder={SUPPLIER.country}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faLocationDot} /> Supplier City
          </label>
          <input
            type="text"
            name="supplierCity"
            value={formData.supplierCity}
            onChange={handleChange}
            placeholder={SUPPLIER.city}
          />
        </div>
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faAnchor} /> Port of Loading
          </label>
          <input
            type="text"
            name="portLoading"
            value={formData.portLoading}
            onChange={handleChange}
            placeholder={SUPPLIER.portLoading}
          />
        </div>
      </div>

      <div className="form-group">
        <label>
          <FontAwesomeIcon icon={faShip} /> Port of Discharge
        </label>
        <input
          type="text"
          name="portDischarge"
          value={formData.portDischarge}
          onChange={handleChange}
          placeholder={SUPPLIER.portDischarge}
        />
      </div>

      <div className="supplier-tip">
        <FontAwesomeIcon icon={faInfoCircle} />
        <span>
          Products are sourced from{" "}
          <strong>{formData.supplierName || SUPPLIER.name}</strong> in{" "}
          <strong>{formData.supplierCountry || SUPPLIER.country}</strong>.
          Shipments arrive via {formData.portLoading || SUPPLIER.portLoading} to{" "}
          {formData.portDischarge || SUPPLIER.portDischarge}.
        </span>
      </div>
    </div>
  );

  const renderSEOTab = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faGlobe} /> SEO
      </h3>

      <div className="form-group">
        <label>Meta Title</label>
        <input
          type="text"
          name="metaTitle"
          value={formData.metaTitle}
          onChange={handleChange}
          placeholder="SEO title"
        />
        <small>{formData.metaTitle.length} characters</small>
      </div>

      <div className="form-group">
        <label>Meta Description</label>
        <textarea
          name="metaDescription"
          value={formData.metaDescription}
          onChange={handleChange}
          rows="2"
          placeholder="SEO description"
        />
        <small>{formData.metaDescription.length} characters</small>
      </div>

      <div className="form-group">
        <label>Meta Keywords</label>
        <input
          type="text"
          name="metaKeywords"
          value={formData.metaKeywords.join(", ")}
          onChange={(e) =>
            setFormData({
              ...formData,
              metaKeywords: e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          placeholder="keyword1, keyword2, keyword3"
        />
        <small>Comma separated keywords</small>
      </div>
    </div>
  );

  const renderStatusTab = () => (
    <div className="form-section">
      <h3>
        <FontAwesomeIcon icon={faCheck} /> Status & Features
      </h3>

      <div className="form-row">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="isAvailable"
            checked={formData.isAvailable}
            onChange={handleChange}
          />
          <FontAwesomeIcon icon={formData.isAvailable ? faCheck : faTimes} />
          Available for Sale
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="isPreOrderAvailable"
            checked={formData.isPreOrderAvailable}
            onChange={handleChange}
          />
          <FontAwesomeIcon
            icon={formData.isPreOrderAvailable ? faCheck : faTimes}
          />
          Available for Pre-Order
        </label>
      </div>

      <div className="form-row">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="featured"
            checked={formData.featured}
            onChange={handleChange}
          />
          <FontAwesomeIcon icon={faStar} />
          Featured Product
        </label>
        {formData.featured && (
          <div className="form-group">
            <label>Featured Order</label>
            <input
              type="number"
              name="featuredOrder"
              value={formData.featuredOrder}
              onChange={handleChange}
              min="0"
              placeholder="Lower numbers appear first"
            />
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Average Rating</label>
          <input
            type="number"
            value={formData.averageRating}
            disabled
            step="0.1"
            min="0"
            max="5"
          />
          <small>Auto-calculated from user reviews</small>
        </div>
        <div className="form-group">
          <label>Total Comments</label>
          <input type="number" value={formData.commentCount} disabled />
          <small>Auto-calculated from user comments</small>
        </div>
      </div>

      <div
        className="supplier-origin-info"
        style={{
          background: "#e8f5e9",
          padding: "0.5rem 0.75rem",
          borderRadius: "8px",
          marginTop: "0.5rem",
        }}
      >
        <FontAwesomeIcon icon={faShip} style={{ color: "#006B2E" }} />
        <span
          style={{
            fontSize: "0.75rem",
            color: "#006B2E",
            marginLeft: "0.5rem",
          }}
        >
          Product sourced from{" "}
          <strong>{formData.supplierName || SUPPLIER.name}</strong> (
          {formData.supplierCountry || SUPPLIER.country})
        </span>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "basic":
        return renderBasicTab();
      case "pricing":
        return renderPricingTab();
      case "descriptions":
        return renderDescriptionsTab();
      case "specs":
        return renderSpecsTab();
      case "inventory":
        return renderInventoryTab();
      case "images":
        return renderImagesTab();
      case "supplier":
        return renderSupplierTab();
      case "seo":
        return renderSEOTab();
      case "status":
        return renderStatusTab();
      default:
        return renderBasicTab();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {product ? (
              <>
                <FontAwesomeIcon icon={faEdit} /> Edit Product
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} /> Add New Product
              </>
            )}
            <span className="supplier-tag">
              <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
            </span>
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {error && (
          <div className="alert-message error">
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}
        {success && (
          <div className="alert-message success">
            <FontAwesomeIcon icon={faCheck} /> {success}
          </div>
        )}

        {renderTabs()}

        <form onSubmit={handleSubmit} className="product-form">
          {renderContent()}

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                </>
              ) : product ? (
                <>
                  <FontAwesomeIcon icon={faSave} /> Update Product
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} /> Create Product
                </>
              )}
            </button>
          </div>
        </form>

        <style>{`
          .modal-overlay {
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(0,0,0,0.5);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 1rem;
          }
          .modal-content {
            background: #fff;
            border-radius: 12px;
            max-width: 1100px;
            width: 100%;
            max-height: 95vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            box-shadow: 0 20px 60px rgba(0,0,0,0.25);
          }
          .modal-content.large { max-width: 1100px; }
          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.25rem 1.5rem;
            border-bottom: 1px solid #e5e5e5;
            flex-shrink: 0;
            flex-wrap: wrap;
            gap: 0.5rem;
          }
          .modal-header h2 {
            color: #006b2e;
            font-size: 1.25rem;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-family: "Montserrat", sans-serif;
          }
          .modal-header h2 svg { color: #d9a321; }
          .supplier-tag {
            font-size: 0.65rem;
            padding: 0.1rem 0.5rem;
            border-radius: 12px;
            background: #dcfce7;
            color: #16a34a;
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
          }
          .close-btn {
            background: none;
            border: none;
            font-size: 1.25rem;
            cursor: pointer;
            color: #6b7280;
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
          }
          .close-btn:hover { background: #f0f0f0; }
          
          .form-tabs {
            display: flex;
            flex-wrap: wrap;
            gap: 0.25rem;
            padding: 0.5rem 1.5rem;
            border-bottom: 1px solid #e5e5e5;
            background: #f8f9fa;
            flex-shrink: 0;
          }
          .form-tabs button {
            padding: 0.35rem 0.75rem;
            border: none;
            border-radius: 4px;
            background: transparent;
            cursor: pointer;
            font-size: 0.75rem;
            color: #5a5a5a;
            transition: all 0.3s ease;
            font-family: "Inter", sans-serif;
          }
          .form-tabs button:hover { background: #e5e5e5; }
          .form-tabs button.active {
            background: #d9a321;
            color: #fff;
          }
          .form-tabs button svg { margin-right: 0.25rem; }
          
          .product-form {
            padding: 1.5rem;
            overflow-y: auto;
            flex: 1;
          }
          .form-section {
            background: #f8f9fa;
            padding: 1.25rem;
            border-radius: 8px;
            margin-bottom: 1.25rem;
            border: 1px solid #e5e5e5;
          }
          .form-section h3 {
            color: #006b2e;
            margin-bottom: 1rem;
            font-size: 1rem;
            font-family: "Montserrat", sans-serif;
          }
          .form-section h3 svg { color: #d9a321; margin-right: 0.5rem; }
          .form-section h4 {
            color: #006b2e;
            margin: 1rem 0 0.75rem;
            font-size: 0.9rem;
            font-family: "Montserrat", sans-serif;
          }
          
          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.75rem;
          }
          .form-group {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          .form-group label {
            font-weight: 500;
            font-size: 0.813rem;
            color: #1f1f1f;
            display: flex;
            align-items: center;
            gap: 0.25rem;
          }
          .form-group label svg { color: #d9a321; }
          .form-group input, .form-group select, .form-group textarea {
            padding: 0.5rem 0.75rem;
            border: 1px solid #e5e5e5;
            border-radius: 6px;
            font-size: 0.875rem;
            background: #fff;
            color: #1f1f1f;
            width: 100%;
            font-family: "Inter", sans-serif;
          }
          .form-group input:focus, .form-group select:focus, .form-group textarea:focus {
            outline: none;
            border-color: #d9a321;
            box-shadow: 0 0 0 3px rgba(217,163,33,0.15);
          }
          .form-group textarea { resize: vertical; min-height: 60px; }
          .form-group input.error, .form-group select.error, .form-group textarea.error {
            border-color: #dc2626;
          }
          .error-text { color: #dc2626; font-size: 0.75rem; margin-top: 0.125rem; }
          .helper-text {
            display: block;
            font-size: 0.65rem;
            color: #9ca3af;
            margin-top: 0.125rem;
          }
          
          .checkbox-label {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            cursor: pointer;
            font-size: 0.875rem;
          }
          .checkbox-label input[type="checkbox"] {
            width: 18px;
            height: 18px;
            cursor: pointer;
            accent-color: #d9a321;
          }
          
          .size-presets {
            display: flex;
            gap: 0.5rem;
            margin-top: 0.25rem;
            flex-wrap: wrap;
          }
          .size-preset-btn {
            padding: 0.15rem 0.6rem;
            border: 1px solid #e5e5e5;
            border-radius: 12px;
            cursor: pointer;
            font-size: 0.7rem;
            transition: all 0.3s ease;
            font-family: "Inter", sans-serif;
          }
          .size-preset-btn:hover { opacity: 0.8; }
          
          .tiers-toggle { margin: 0.75rem 0 0.5rem; }
          .toggle-btn {
            background: none;
            border: none;
            cursor: pointer;
            color: #006b2e;
            font-weight: 500;
            font-size: 0.875rem;
            padding: 0.25rem 0;
            font-family: "Inter", sans-serif;
          }
          .toggle-btn:hover { color: #d9a321; }
          
          .tiers-header, .bulk-header {
            display: grid;
            grid-template-columns: 100px 80px 80px 80px 40px;
            gap: 0.5rem;
            font-size: 0.75rem;
            font-weight: 600;
            color: #5a5a5a;
            padding: 0.25rem 0;
            border-bottom: 1px solid #e5e5e5;
            margin-bottom: 0.25rem;
          }
          .tier-row, .bulk-row {
            display: grid;
            grid-template-columns: 100px 80px 80px 80px 40px;
            gap: 0.5rem;
            align-items: center;
            padding: 0.25rem 0;
          }
          .tier-row input, .tier-row select,
          .bulk-row input, .bulk-row select {
            padding: 0.25rem 0.4rem;
            border: 1px solid #e5e5e5;
            border-radius: 4px;
            font-size: 0.75rem;
            width: 100%;
            font-family: "Inter", sans-serif;
          }
          .tier-row input.error, .bulk-row input.error {
            border-color: #dc2626;
          }
          .remove-btn {
            background: none;
            border: none;
            color: #dc2626;
            cursor: pointer;
            padding: 0.2rem;
          }
          .remove-btn:hover { opacity: 0.7; }
          .remove-btn:disabled { opacity: 0.3; cursor: not-allowed; }
          
          .add-btn {
            padding: 0.25rem 0.75rem;
            background: #006b2e;
            color: #fff;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.75rem;
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
            margin-top: 0.5rem;
            font-family: "Inter", sans-serif;
          }
          .add-btn:hover { background: #014421; }
          
          .tags-list {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-bottom: 0.5rem;
          }
          .tag-item {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            background: #e8f5e9;
            padding: 0.2rem 0.6rem;
            border-radius: 12px;
            font-size: 0.8rem;
          }
          .tag-item button {
            background: none;
            border: none;
            color: #5a5a5a;
            cursor: pointer;
            padding: 0.1rem;
          }
          .tag-item button:hover { color: #dc2626; }
          
          .add-tag-row, .add-spec-row {
            display: flex;
            gap: 0.5rem;
          }
          .add-tag-row input, .add-spec-row input {
            flex: 1;
            padding: 0.4rem 0.6rem;
            border: 1px solid #e5e5e5;
            border-radius: 4px;
            font-size: 0.8rem;
            font-family: "Inter", sans-serif;
          }
          
          .specs-list {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            margin-bottom: 0.5rem;
          }
          .spec-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.25rem 0.5rem;
            background: #fff;
            border-radius: 4px;
            border: 1px solid #e5e5e5;
          }
          .spec-key { font-weight: 600; color: #006b2e; }
          .spec-value { color: #1f1f1f; }
          .spec-item button {
            margin-left: auto;
            background: none;
            border: none;
            color: #dc2626;
            cursor: pointer;
          }
          
          .file-upload-area {
            border: 2px dashed #e5e5e5;
            border-radius: 8px;
            padding: 2rem;
            text-align: center;
            transition: border-color 0.3s ease;
            position: relative;
          }
          .file-upload-area:hover { border-color: #d9a321; }
          .file-input {
            position: absolute;
            opacity: 0;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            cursor: pointer;
          }
          .file-upload-label {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.5rem;
            cursor: pointer;
            color: #5a5a5a;
          }
          .file-upload-label svg { font-size: 2rem; color: #d9a321; }
          .file-upload-label small { font-size: 0.7rem; color: #9ca3af; }
          
          .image-preview-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
            gap: 0.5rem;
            margin-top: 0.75rem;
          }
          .image-preview-item {
            position: relative;
            aspect-ratio: 1;
            border-radius: 4px;
            overflow: hidden;
            border: 1px solid #e5e5e5;
          }
          .image-preview-item img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .image-preview-item button {
            position: absolute;
            top: 2px;
            right: 2px;
            background: rgba(0,0,0,0.5);
            border: none;
            color: #fff;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
          }
          .image-preview-item button:hover { background: rgba(220,38,38,0.8); }
          
          .supplier-info-card {
            background: linear-gradient(135deg, #006B2E, #014421);
            padding: 0.75rem 1.25rem;
            border-radius: 12px;
            display: flex;
            align-items: center;
            gap: 1rem;
            color: #ffffff;
            margin-bottom: 1rem;
          }
          .supplier-info-card .supplier-icon {
            font-size: 1.5rem;
            color: #D9A321;
          }
          .supplier-info-card .supplier-info {
            flex: 1;
          }
          .supplier-info-card .supplier-info strong {
            font-size: 0.875rem;
            font-family: "Montserrat", sans-serif;
          }
          .supplier-info-card .supplier-info span {
            font-size: 0.65rem;
            opacity: 0.8;
            display: block;
          }
          .supplier-info-card .supplier-info span svg { margin: 0 0.25rem; }
          .supplier-badge {
            font-size: 0.6rem;
            background: rgba(217,163,33,0.2);
            color: #D9A321;
            padding: 0.15rem 0.6rem;
            border-radius: 12px;
            display: flex;
            align-items: center;
            gap: 0.25rem;
          }
          .supplier-tip {
            margin-top: 0.75rem;
            padding: 0.5rem 0.75rem;
            background: #f0f9ff;
            border-radius: 8px;
            font-size: 0.75rem;
            color: #1e40af;
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;
            border-left: 3px solid #3b82f6;
          }
          .supplier-tip svg { color: #3b82f6; margin-top: 0.1rem; }
          
          .form-actions {
            display: flex;
            gap: 0.75rem;
            justify-content: flex-end;
            padding-top: 1rem;
            border-top: 1px solid #e5e5e5;
            margin-top: 1rem;
          }
          .btn-primary {
            padding: 0.5rem 1.5rem;
            background: #d9a321;
            color: #fff;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: all 0.3s ease;
            font-family: "Inter", sans-serif;
          }
          .btn-primary:hover:not(:disabled) {
            background: #b8860b;
            transform: translateY(-2px);
          }
          .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
          .btn-secondary {
            padding: 0.5rem 1.5rem;
            background: #f0f0f0;
            color: #1f1f1f;
            border: 1px solid #e5e5e5;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 500;
            font-family: "Inter", sans-serif;
          }
          .btn-secondary:hover { background: #e5e5e5; }
          
          .alert-message {
            padding: 0.75rem 1rem;
            border-radius: 6px;
            margin: 0.5rem 1.5rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
          .alert-message.error {
            background: #fee2e2;
            color: #dc2626;
            border: 1px solid #fecaca;
          }
          .alert-message.success {
            background: #dcfce7;
            color: #16a34a;
            border: 1px solid #bbf7d0;
          }
          
          @media (max-width: 768px) {
            .form-row, .tiers-header, .tier-row, .bulk-header, .bulk-row {
              grid-template-columns: 1fr;
            }
            .form-tabs { flex-wrap: wrap; }
            .form-tabs button { font-size: 0.65rem; padding: 0.25rem 0.5rem; }
            .modal-content.large { max-width: 100%; margin: 0.5rem; border-radius: 8px; }
            .modal-header { padding: 0.75rem 1rem; }
            .modal-header h2 { font-size: 1rem; }
            .product-form { padding: 1rem; }
            .form-section { padding: 0.75rem; }
            .supplier-info-card { flex-wrap: wrap; justify-content: center; text-align: center; }
            .file-upload-area { padding: 1rem; }
          }
          
          @media (max-width: 480px) {
            .form-tabs button { font-size: 0.6rem; padding: 0.2rem 0.4rem; }
            .tier-row, .bulk-row { grid-template-columns: 1fr; gap: 0.25rem; }
            .add-tag-row, .add-spec-row { flex-wrap: wrap; }
            .form-actions { flex-direction: column; }
            .form-actions button { width: 100%; justify-content: center; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default ProductForm;
